// NewsWebSocketInitializer.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FeedActionContext } from './FeedState';
import { useSubscribedSymbolsContext } from './context/DataFeedSymbolsContext';
import { newsWebSocketService } from './NewsWebSocketService';
import { useStateSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useNewsFeedStatus, useNewsWebSocketCloseReason } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const NewsWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.newsFeed;
  const newsFeedStatus = useNewsFeedStatus();
  const closeReason = useNewsWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      newsWebSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (newsWebSocketService.shouldTryReconnect()) {
          newsWebSocketService.connect(dispatch, dispatcher, apiToken).then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (newsFeedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `News Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'News Socket is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [newsFeedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect && apiToken) {
      console.log('initializing news websocket (this should only happen once!)');
      newsWebSocketService.connect(dispatch, dispatcher, apiToken);
    } else if (!canConnect && !newsWebSocketService.shouldTryReconnect()) {
      console.log('disconnecting news websocket because user can no longer connect');
      newsWebSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect, apiToken]);

  return null; // This component does not render anything
};

export default NewsWebSocketInitializer;
